import { setupDiscordSdk, isRunningInDiscord, openPresenterLink, getChannelId } from './discord.js';
import {
  initSocketConnection,
  registerUserInSocket,
  sendReaction,
  raiseHand,
} from './webrtc.js';

let currentUser = null;
let currentChannelId = 'default-room';
let audioContext = null;
let analyser = null;
let audioMeterInterval = null;
let nextPcmPlayTime = 0;
let socketInstance = null;

let currentGrantedGuest = null;
let isAdminOnlineInRoom = false;
let currentPresenterState = null;

const MASTER_ADMIN_ID = '1490438398609985597';
const MASTER_ADMIN_USERNAME = 'gally_neko_to_ryuu';

function isMasterAdmin(user) {
  if (!user) return false;
  const uId = String(user.id || '');
  const uName = String(user.username || '').toLowerCase();
  return uId === MASTER_ADMIN_ID || uName === MASTER_ADMIN_USERNAME.toLowerCase();
}

// Elementos da DOM
const accessDeniedScreen = document.getElementById('access-denied-screen');
const accessDeniedTitle = document.getElementById('access-denied-title');
const accessDeniedMsg = document.getElementById('access-denied-msg');
const appContainer = document.getElementById('app');

const statusBadge = document.getElementById('status-badge');
const statusText = document.getElementById('status-text');
const roomIdDisplay = document.getElementById('room-id-display');
const userAvatar = document.getElementById('user-avatar');
const userName = document.getElementById('user-name');
const userRole = document.getElementById('user-role');

const idleState = document.getElementById('idle-state');
const videoContainer = document.getElementById('video-container');
const remoteVideo = document.getElementById('remote-video');
const remoteImage = document.getElementById('remote-image');
const presenterTag = document.getElementById('presenter-tag');
const presenterName = document.getElementById('presenter-name');

const usersList = document.getElementById('users-list');
const viewerCount = document.getElementById('viewer-count');

const btnOpenPresenterLink = document.getElementById('btn-open-presenter-link');
const btnToggleAudio = document.getElementById('btn-toggle-audio');
const audioIcon = document.getElementById('audio-icon');
const audioLabel = document.getElementById('audio-label');
const btnFullscreen = document.getElementById('btn-fullscreen');
const btnRaiseHand = document.getElementById('btn-raise-hand');
const reactionsOverlay = document.getElementById('reactions-overlay');

// Bloqueia o acesso fora do Discord
function blockAccess(title, message) {
  if (accessDeniedTitle) accessDeniedTitle.textContent = title;
  if (accessDeniedMsg) accessDeniedMsg.textContent = message;
  if (accessDeniedScreen) {
    accessDeniedScreen.classList.remove('hidden');
    accessDeniedScreen.style.display = 'flex';
  }
  if (appContainer) appContainer.classList.add('hidden');
}

// Reproduz pacotes de áudio PCM brutos em tempo real via WebAudio API
function onPcmAudioChunk(arrayBuffer) {
  try {
    // Se o usuário logado for o próprio apresentador, ignora o áudio transmitido por ele mesmo para evitar eco/duplicação
    if (currentPresenterState && currentUser && String(currentPresenterState.user?.id) === String(currentUser.id)) {
      return;
    }

    if (!audioContext) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContext = new AudioCtx({ sampleRate: 22050 });
    }
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const int16Array = new Int16Array(arrayBuffer);
    if (int16Array.length === 0) return;

    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    const buffer = audioContext.createBuffer(1, float32Array.length, 22050);
    buffer.getChannelData(0).set(float32Array);

    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);

    const now = audioContext.currentTime;
    if (nextPcmPlayTime < now) {
      nextPcmPlayTime = now;
    }
    source.start(nextPcmPlayTime);
    nextPcmPlayTime += buffer.duration;

    if (audioLabel) audioLabel.textContent = 'Som Transmitido ao Vivo (WebAudio PCM)';
    if (audioIcon) audioIcon.textContent = '🔊';
  } catch (err) {
    console.warn('[PCM Audio Play Error]', err);
  }
}

// Medidor visual de áudio WebAudio
function setupAudioLevelMeter(stream) {
  try {
    if (!audioContext) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContext = new AudioCtx({ sampleRate: 22050 });
    }
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    const source = audioContext.createMediaStreamSource(stream);
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    if (audioMeterInterval) clearInterval(audioMeterInterval);

    audioMeterInterval = setInterval(() => {
      analyser.getByteFrequencyData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const average = sum / dataArray.length;
      const volumePercentage = Math.min(100, Math.round((average / 128) * 100));

      if (volumePercentage > 2) {
        if (audioLabel) audioLabel.textContent = `Som WebRTC: ${volumePercentage}%`;
        if (audioIcon) audioIcon.textContent = '🔊';
      }
    }, 150);
  } catch (err) {
    console.warn('[WebAudio Meter Error]', err);
  }
}

function unlockBrowserAudio() {
  if (!audioContext) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioCtx({ sampleRate: 22050 });
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(() => {
      console.log('[AudioContext] Resumido pelo clique do usuário.');
    });
  }

  if (remoteVideo) {
    remoteVideo.muted = false;
    remoteVideo.volume = 1.0;
    remoteVideo.play().catch((e) => console.warn(e));
    if (audioIcon) audioIcon.textContent = '🔊';
    if (btnToggleAudio) {
      btnToggleAudio.classList.remove('btn-secondary');
      btnToggleAudio.classList.add('btn-primary');
    }
  }
}

const ALLOWED_GUILD_ID = '1497660953876238487';
const ALLOWED_ROLE_ID = '1548067214928777316';

function hasRolePermission(user) {
  if (!user) return false;
  const roles = Array.isArray(user.roles) ? user.roles.map(String) : [];
  return roles.includes(ALLOWED_ROLE_ID);
}

function updatePresenterButtonState() {
  if (!btnOpenPresenterLink) return;

  const isAdmin = isMasterAdmin(currentUser);
  const hasRole = hasRolePermission(currentUser);
  const myId = currentUser ? String(currentUser.id) : '';
  const isGranted = currentGrantedGuest && String(currentGrantedGuest.userId) === myId;

  if (isAdmin) {
    btnOpenPresenterLink.innerHTML = '<span class="btn-icon">👑</span> Quero Apresentar Minha Tela (Dona do Palco)';
    btnOpenPresenterLink.classList.remove('btn-secondary');
    btnOpenPresenterLink.classList.add('btn-primary');
  } else if (hasRole) {
    btnOpenPresenterLink.innerHTML = '<span class="btn-icon">⭐</span> Quero Apresentar Minha Tela (Cargo Autorizado)';
    btnOpenPresenterLink.classList.remove('btn-secondary');
    btnOpenPresenterLink.classList.add('btn-primary');
  } else if (isGranted) {
    btnOpenPresenterLink.innerHTML = '<span class="btn-icon">🎤</span> Quero Apresentar Minha Tela (Convidado Autorizado)';
    btnOpenPresenterLink.classList.remove('btn-secondary');
    btnOpenPresenterLink.classList.add('btn-primary');
  } else {
    btnOpenPresenterLink.innerHTML = `<span class="btn-icon">🔒</span> Transmissão Reservada (Peça permissão a @${MASTER_ADMIN_USERNAME})`;
    btnOpenPresenterLink.classList.remove('btn-primary');
    btnOpenPresenterLink.classList.add('btn-secondary');
  }
}

function setupEventListeners() {
  console.log('[Main] Vinculando ouvintes de eventos...');

  document.addEventListener('click', unlockBrowserAudio, { once: false });

  // Botão para abrir o painel do apresentador (Admin, Cargo Autorizado ou Convidado)
  if (btnOpenPresenterLink) {
    btnOpenPresenterLink.addEventListener('click', () => {
      const isAdmin = isMasterAdmin(currentUser);
      const hasRole = hasRolePermission(currentUser);
      const myId = currentUser ? String(currentUser.id) : '';
      const isGranted = currentGrantedGuest && String(currentGrantedGuest.userId) === myId;

      if (!isAdmin && !hasRole && !isGranted) {
        alert(`⛔ Apenas a administradora do palco (@${MASTER_ADMIN_USERNAME}), membros com o cargo autorizado ou convidados liberados podem transmitir nesta sala.`);
        return;
      }

      if (currentPresenterState) {
        alert('Já existe uma transmissão ao vivo acontecendo no momento. Aguarde o encerramento para apresentar.');
        return;
      }

      const room = currentChannelId || getChannelId() || 'default-room';
      console.log(`[Main] Gerando link de transmissão para a sala [${room}]...`);
      openPresenterLink(room, currentUser);
    });
  }

  // Controle de Áudio / Mute do Espectador
  if (btnToggleAudio && remoteVideo) {
    btnToggleAudio.addEventListener('click', (e) => {
      e.stopPropagation();
      remoteVideo.muted = !remoteVideo.muted;
      if (remoteVideo.muted) {
        if (audioIcon) audioIcon.textContent = '🔇';
        if (audioLabel) audioLabel.textContent = 'Som Mutado';
        btnToggleAudio.classList.remove('btn-primary');
        btnToggleAudio.classList.add('btn-secondary');
      } else {
        remoteVideo.volume = 1.0;
        remoteVideo.play().catch((err) => console.warn(err));
        if (audioIcon) audioIcon.textContent = '🔊';
        if (audioLabel) audioLabel.textContent = 'Som Ativado';
        btnToggleAudio.classList.remove('btn-secondary');
        btnToggleAudio.classList.add('btn-primary');
      }
    });
  }

  // Tela Cheia / Modo Foco Nativo do Discord
  const btnExitFullscreen = document.getElementById('btn-exit-fullscreen');
  const fullscreenBtnIcon = document.getElementById('fullscreen-btn-icon');
  const fullscreenBtnText = document.getElementById('fullscreen-btn-text');

  function toggleTheaterMode() {
    if (!appContainer) return;
    const isTheater = appContainer.classList.toggle('theater-mode');
    console.log('[Main] Modo Foco/Cinema alternado:', isTheater);

    if (fullscreenBtnIcon) fullscreenBtnIcon.textContent = isTheater ? '↙️' : '⛶';
    if (fullscreenBtnText) fullscreenBtnText.textContent = isTheater ? 'Restaurar' : 'Tela Cheia';
  }

  if (btnFullscreen) {
    btnFullscreen.addEventListener('click', toggleTheaterMode);
  }

  if (btnExitFullscreen) {
    btnExitFullscreen.addEventListener('click', toggleTheaterMode);
  }

  // Reações Rápidas
  document.querySelectorAll('.btn-reaction').forEach((btn) => {
    btn.addEventListener('click', () => {
      const emoji = btn.getAttribute('data-emoji');
      sendReaction(emoji);
    });
  });

  // Levantar a Mão
  if (btnRaiseHand) {
    btnRaiseHand.addEventListener('click', () => {
      raiseHand();
      btnRaiseHand.textContent = '✋ Mão Levantada!';
      setTimeout(() => {
        btnRaiseHand.textContent = '✋ Levantar a Mão';
      }, 3000);
    });
  }
}

// Inicialização da Aplicação
async function init() {
  console.log('[Main] Inicializando aplicativo de Apresentação Online...');
  
  setupEventListeners();

  if (!isRunningInDiscord()) {
    blockAccess(
      'Acesso Restrito ao Discord',
      'Este site não pode ser acessado diretamente pelo navegador. Ele funciona exclusivamente como uma Atividade dentro dos canais de voz do aplicativo do Discord.'
    );
    return;
  }

  const { user, channelId } = await setupDiscordSdk();
  
  if (!user || user.id === 'error-user') {
    blockAccess(
      'Falha na Autenticação',
      'Não foi possível validar suas credenciais do Discord. Certifique-se de que o DISCORD_CLIENT_ID e o DISCORD_CLIENT_SECRET estão configurados no servidor.'
    );
    return;
  }

  currentUser = user;
  currentChannelId = channelId || getChannelId() || 'default-room';

  console.log(`[Main] Conectando à sala isolada definitiva: [${currentChannelId}]`);

  if (roomIdDisplay) roomIdDisplay.textContent = `#${currentChannelId}`;
  if (userAvatar) userAvatar.src = currentUser.avatar;
  if (userName) userName.textContent = currentUser.username;
  if (statusBadge) statusBadge.style.backgroundColor = 'rgba(35, 165, 90, 0.2)';
  if (statusText) statusText.textContent = 'Autenticado no Discord';

  const isAdmin = isMasterAdmin(currentUser);
  if (userRole) {
    userRole.textContent = isAdmin ? '👑 Administradora do Palco' : 'Espectador';
  }

  updatePresenterButtonState();

  socketInstance = await initSocketConnection(
    onSessionState,
    onUserListUpdated,
    onPresentationStarted,
    onPresentationStopped,
    onNewReaction,
    onUserRaisedHand,
    onSlideStarted,
    onStreamFrame,
    setupAudioLevelMeter,
    onPcmAudioChunk
  );

  if (socketInstance) {
    socketInstance.on('stage-permission-updated', ({ grantedGuest, isAdminOnline }) => {
      console.log('[Socket] Permissões de palco atualizadas:', grantedGuest);
      currentGrantedGuest = grantedGuest;
      isAdminOnlineInRoom = isAdminOnline;
      updatePresenterButtonState();

      // Recarrega a lista de usuários para atualizar os botões de conceder/revogar da Admin
      const currentList = Array.from(document.querySelectorAll('.user-item')).map(() => null);
      if (currentList.length > 0 && socketInstance) {
        socketInstance.emit('request-user-list-update');
      }
    });

    socketInstance.on('error-notice', (msg) => {
      alert(msg);
    });
  }

  registerUserInSocket(currentUser, currentChannelId);
}

function onSessionState({ currentPresenter, lastStreamFrame, totalViewers, grantedGuest, isAdminOnline }) {
  console.log('[Main] onSessionState:', currentPresenter);
  currentPresenterState = currentPresenter;
  currentGrantedGuest = grantedGuest;
  isAdminOnlineInRoom = isAdminOnline;

  updatePresenterButtonState();

  if (currentPresenter) {
    if (currentPresenter.imageBase64) {
      onSlideStarted({ presenter: currentPresenter, imageBase64: currentPresenter.imageBase64 });
    } else {
      onPresentationStarted({ presenter: currentPresenter });
      if (lastStreamFrame) {
        onStreamFrame(lastStreamFrame);
      }
    }
  } else {
    onPresentationStopped();
  }
}

function onUserListUpdated(users) {
  if (viewerCount) viewerCount.textContent = users.length;
  if (!usersList) return;

  const isAdmin = isMasterAdmin(currentUser);
  usersList.innerHTML = '';

  users.forEach(({ user }) => {
    const li = document.createElement('li');
    li.className = 'user-item';

    const targetId = String(user.id);
    const targetName = user.username;
    const isTargetAdmin = isMasterAdmin(user);
    const isTargetRoleUser = hasRolePermission(user);
    const isGranted = currentGrantedGuest && String(currentGrantedGuest.userId) === targetId;

    let adminActionBtnHtml = '';

    if (isAdmin && !isTargetAdmin && !isTargetRoleUser) {
      if (isGranted) {
        adminActionBtnHtml = `<button type="button" class="btn btn-danger btn-xs btn-revoke-stage" data-userid="${targetId}" style="padding: 2px 8px; font-size: 0.7rem; margin-left: auto;">🚫 Revogar</button>`;
      } else {
        adminActionBtnHtml = `<button type="button" class="btn btn-primary btn-xs btn-grant-stage" data-userid="${targetId}" data-username="${targetName}" style="padding: 2px 8px; font-size: 0.7rem; margin-left: auto;">🎤 Conceder</button>`;
      }
    } else if (isTargetRoleUser && !isTargetAdmin) {
      adminActionBtnHtml = `<span style="font-size: 0.75rem; color: #f0b232; margin-left: auto; font-weight: 600;">⭐ Transmissor</span>`;
    }

    li.innerHTML = `
      <img class="user-item-avatar" src="${user.avatar || 'https://cdn.discordapp.com/embed/avatars/0.png'}" alt="${user.username}" />
      <span style="font-weight: ${isTargetAdmin || isTargetRoleUser ? '700' : '400'}; color: ${isTargetAdmin ? '#5865f2' : isTargetRoleUser ? '#f0b232' : 'inherit'};">
        ${user.username} ${isTargetAdmin ? '👑' : ''} ${isTargetRoleUser ? '⭐' : ''} ${isGranted ? '🎤' : ''}
      </span>
      ${adminActionBtnHtml}
    `;

    usersList.appendChild(li);
  });

  // Eventos dos botões da Administradora
  if (isAdmin) {
    document.querySelectorAll('.btn-grant-stage').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const targetUserId = btn.getAttribute('data-userid');
        const targetUsername = btn.getAttribute('data-username');
        console.log(`[Admin] Concedendo palco para ID ${targetUserId} (@${targetUsername})...`);
        if (socketInstance) {
          socketInstance.emit('grant-stage-permission', { targetUserId, targetUsername });
        }
      });
    });

    document.querySelectorAll('.btn-revoke-stage').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        console.log('[Admin] Revogando permissões de palco do convidado...');
        if (socketInstance) {
          socketInstance.emit('revoke-stage-permission');
        }
      });
    });
  }
}

function onPresentationStarted({ presenter }) {
  console.log('[Main] Apresentação Iniciada por:', presenter);
  currentPresenterState = presenter;
  
  if (remoteVideo) {
    const isSelf = currentUser && presenter.user && String(currentUser.id) === String(presenter.user.id);
    remoteVideo.muted = isSelf;
  }
  
  showVideoViewport(presenter.user ? presenter.user.username : 'Apresentador');
}

function onSlideStarted({ presenter, imageBase64 }) {
  console.log('[Main] Slide recebido do apresentador!');
  currentPresenterState = presenter;
  if (remoteVideo) {
    remoteVideo.style.display = 'none';
    remoteVideo.classList.add('hidden');
  }
  if (remoteImage) {
    remoteImage.src = imageBase64;
    remoteImage.style.display = 'block';
    remoteImage.classList.remove('hidden');
  }
  showVideoViewport(presenter.user ? presenter.user.username : 'Apresentador');
}

function onStreamFrame(frameData) {
  if (!currentPresenterState) {
    return;
  }

  if (remoteVideo && remoteVideo.srcObject && remoteVideo.videoWidth > 0) {
    return;
  }

  if (remoteImage) {
    remoteImage.src = frameData;
    remoteImage.style.display = 'block';
    remoteImage.classList.remove('hidden');
  }
  if (remoteVideo) {
    remoteVideo.style.display = 'none';
    remoteVideo.classList.add('hidden');
  }
  showVideoViewport(currentPresenterState.user ? currentPresenterState.user.username : 'Apresentador');
}

function onPresentationStopped() {
  currentPresenterState = null;
  if (audioMeterInterval) clearInterval(audioMeterInterval);
  handlePresentationEndedUI();
}

function showVideoViewport(name) {
  if (idleState) {
    idleState.classList.add('hidden');
    idleState.style.display = 'none';
  }
  if (videoContainer) {
    videoContainer.classList.remove('hidden');
    videoContainer.style.display = 'flex';
  }
  if (presenterName) presenterName.textContent = name;
}

function handlePresentationEndedUI() {
  currentPresenterState = null;

  if (userRole) {
    const isAdmin = isMasterAdmin(currentUser);
    userRole.textContent = isAdmin ? '👑 Administradora do Palco' : 'Espectador';
  }
  
  if (remoteVideo) {
    remoteVideo.srcObject = null;
    remoteVideo.style.display = 'none';
    remoteVideo.classList.add('hidden');
  }

  if (remoteImage) {
    remoteImage.src = '';
    remoteImage.style.display = 'none';
    remoteImage.classList.add('hidden');
  }

  if (videoContainer) {
    videoContainer.classList.add('hidden');
    videoContainer.style.display = 'none';
  }

  if (idleState) {
    idleState.classList.remove('hidden');
    idleState.style.display = 'flex';
  }
}

function onNewReaction({ emoji, user }) {
  if (!reactionsOverlay) return;
  const el = document.createElement('div');
  el.className = 'floating-emoji';
  el.textContent = emoji;
  el.style.left = `${Math.random() * 80 + 10}%`;

  reactionsOverlay.appendChild(el);

  setTimeout(() => {
    el.remove();
  }, 2500);
}

function onUserRaisedHand(user) {
  if (!reactionsOverlay) return;
  const name = user ? user.username : 'Alguém';
  const toast = document.createElement('div');
  toast.className = 'live-indicator';
  toast.style.backgroundColor = 'rgba(88, 101, 242, 0.9)';
  toast.style.top = '60px';
  toast.textContent = `✋ ${name} levantou a mão!`;

  reactionsOverlay.appendChild(toast);
  setTimeout(() => toast.remove(), 4000);
}

// Iniciar a aplicação
init();
